/** Vector mask geometry — Tier 2 Group A PR-D.
 *
 *  Two responsibilities live here:
 *
 *  1. `rasterizeVectorPathToMaskCanvas` — fill a vector path's
 *     geometry into a mask's RAW alpha canvas. Vector masks plug
 *     into the SAME raw/effective split that PR #238 introduced
 *     for raster masks: the path's filled region produces opaque
 *     white in RAW (revealing the underlying layer), and the
 *     subsequent `materializeMaskClipPath` pass derives the
 *     EFFECTIVE alpha with feather / invert / density applied.
 *     The slow-path compositor + Fabric's native clipPath
 *     compositing both stay unchanged.
 *
 *  2. `traceMaskAlphaToVectorPath` — convert an existing RAW
 *     alpha canvas back into a vector path so the raster→vector
 *     menu entry produces an editable path instead of starting
 *     from a blank canvas. Uses marching-squares on the binary-
 *     thresholded alpha to extract contours, then runs each
 *     contour through paper.js's `Path.simplify()` to convert
 *     the polyline into smooth Bezier curves.
 *
 *  Both functions are pure (no DOM / React / Fabric coupling)
 *  beyond the canvas2d context they're handed. Same convention
 *  as `lib/path-ops.ts` and `lib/vector-path.ts`.
 *
 *  The rasterizer accepts SVG path data (already the lingua
 *  franca via `pathToSvgData` in `vector-path.ts`) so it can use
 *  the browser's native `Path2D` parser — no manual path command
 *  walking needed. Path2D's coverage of M/L/C/Z is exactly the
 *  subset `pathToSvgData` emits. */

import paper from 'paper/dist/paper-core';
import type { VectorPath } from '../types';
import { pathToSvgData } from './vector-path';
import { paperPathItemToVectorPath } from './path-ops';
import { ensurePaperInitialized } from './paper-bootstrap';

/** Fill `svgData` into `target`'s alpha. Target is cleared first
 *  and the result is opaque white inside the filled region,
 *  transparent outside — exactly the "reveal-only-inside-the-path"
 *  semantic Photoshop / Affinity vector masks have.
 *
 *  Path2D uses the canvas2d default fill rule (`'nonzero'`). The
 *  `pathToSvgData` output and paper.js's output both use the same
 *  rule, so multi-sub-path holes from boolean-op results render
 *  correctly without configuration.
 *
 *  Empty path data (no sub-paths, or anchors collapsed to zero
 *  length) leaves the target fully transparent — the mask hides
 *  everything. This is the desired "freshly-converted empty
 *  vector mask" state. */
export function rasterizeVectorPathToMaskCanvas(
  target: HTMLCanvasElement,
  svgData: string,
  width: number,
  height: number,
): void {
  const ctx = target.getContext('2d', { willReadFrequently: true });
  if (!ctx) return;
  ctx.save();
  ctx.globalAlpha = 1;
  ctx.globalCompositeOperation = 'source-over';
  ctx.filter = 'none';
  ctx.clearRect(0, 0, width, height);
  if (svgData.length > 0) {
    const path2d = new Path2D(svgData);
    ctx.fillStyle = '#ffffff';
    ctx.fill(path2d);
  }
  ctx.restore();
}

/** Convenience wrapper — accept a `VectorPath` directly. Same
 *  semantics as the SVG-data variant; spares callers from
 *  threading `pathToSvgData` themselves. */
export function rasterizeVectorMaskInto(
  target: HTMLCanvasElement,
  path: VectorPath,
  width: number,
  height: number,
): void {
  rasterizeVectorPathToMaskCanvas(target, pathToSvgData(path), width, height);
}

/** Build a canvas-sized rectangle vector path. Used as the seed
 *  for raster → vector conversion when the existing RAW alpha is
 *  empty or fully opaque — both states mean "reveal everything"
 *  and a rect-covering-canvas is the lossless vector
 *  representation. Returns a single closed sub-path with 4 corner
 *  anchors. */
export function canvasRectPath(width: number, height: number): VectorPath {
  return {
    subPaths: [
      {
        closed: true,
        anchors: [
          { x: 0, y: 0, type: 'corner' },
          { x: width, y: 0, type: 'corner' },
          { x: width, y: height, type: 'corner' },
          { x: 0, y: height, type: 'corner' },
        ],
      },
    ],
  };
}

// ─── Raster → Vector tracing ────────────────────────────────────

/** Trace the binary-thresholded alpha of `source` into a
 *  `VectorPath`. Used by the raster → vector conversion path so
 *  the user's existing mask paint isn't lost when they switch
 *  kinds.
 *
 *  Pipeline:
 *    1. Read `source`'s alpha channel; threshold at `alphaThreshold`
 *       (default 128 — Photoshop's selection threshold).
 *    2. Marching-squares to extract per-cell line segments at
 *       half-pixel positions. Connected segments form closed
 *       contour loops (one per connected component, both outer
 *       and inner / hole edges).
 *    3. Feed each loop into a `paper.Path`, mark closed, call
 *       `Path.simplify(tolerance)` to convert the polyline into
 *       Bezier curves with anchors at high-curvature points
 *       only.
 *    4. Compound the simplified paths and round-trip through
 *       `paperPathItemToVectorPath` so the result lives in the
 *       same `VectorPath` shape every other vector op consumes.
 *
 *  When `source`'s alpha is uniformly above or below the
 *  threshold the function returns either a canvas-rect path (all
 *  above — fully revealed) or an empty path (all below — fully
 *  hidden) without invoking paper. Same explicit fast-paths skip
 *  the paper bootstrap cost.
 *
 *  The fully-revealed case maps to a rect because every reader
 *  downstream treats "no path" as "hide everything." A traced
 *  vector mask must produce the same visual result the raster
 *  mask did. */
export function traceMaskAlphaToVectorPath(
  source: HTMLCanvasElement,
  options?: { alphaThreshold?: number; simplifyTolerance?: number },
): VectorPath {
  const width = source.width;
  const height = source.height;
  if (width === 0 || height === 0) return { subPaths: [] };
  const ctx = source.getContext('2d', { willReadFrequently: true });
  if (!ctx) return { subPaths: [] };
  const id = ctx.getImageData(0, 0, width, height);
  return traceAlphaImageDataToVectorPath(id.data, width, height, options);
}

/** Pure-function counterpart to `traceMaskAlphaToVectorPath` —
 *  accepts an RGBA pixel buffer + dimensions directly (no canvas
 *  context). The HTMLCanvasElement wrapper is the public-facing
 *  API; this signature exists so unit tests can drive the trace
 *  without happy-dom's missing canvas2d backend. Same semantics
 *  as the wrapper. */
export function traceAlphaImageDataToVectorPath(
  rgba: Uint8ClampedArray | Uint8Array,
  width: number,
  height: number,
  options?: { alphaThreshold?: number; simplifyTolerance?: number },
): VectorPath {
  if (width === 0 || height === 0) return { subPaths: [] };
  const threshold = options?.alphaThreshold ?? 128;

  // Build a (width+2) × (height+2) padded binary grid. Padding by
  // one cell on every side guarantees that any contour touching
  // the canvas edge becomes a closed loop in marching-squares
  // output instead of opening to the edge — same trick every
  // image-tracing lib uses.
  const pw = width + 2;
  const ph = height + 2;
  const grid = new Uint8Array(pw * ph);
  let above = 0;
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const a = rgba[(y * width + x) * 4 + 3];
      if (a >= threshold) {
        grid[(y + 1) * pw + (x + 1)] = 1;
        above++;
      }
    }
  }
  if (above === 0) return { subPaths: [] };
  if (above === width * height) return canvasRectPath(width, height);

  const contours = marchingSquaresContours(grid, pw, ph);
  if (contours.length === 0) return { subPaths: [] };

  ensurePaperInitialized();
  const tol = options?.simplifyTolerance ?? 2.5;
  const compound = new paper.CompoundPath({ insert: false });
  for (const contour of contours) {
    if (contour.length < 3) continue;
    // Translate back from padded grid coords (1-based) into
    // canvas pixel coords (0-based). Marching-squares emits
    // edge midpoints which already live on integer + 0.5
    // half-grid lines; the shift keeps the curve aligned with
    // the underlying pixel edges.
    const segments = contour.map((p) => new paper.Point(p.x - 1, p.y - 1));
    const sub = new paper.Path({ segments, closed: true, insert: false });
    // paper.Path.simplify converts the polyline's many anchors
    // into a small number of smooth Bezier anchors. Lower
    // tolerance = more anchors / closer to the polyline; higher
    // tolerance = fewer anchors / smoother + lossy. The default
    // is empirically chosen against typical hand-painted masks
    // — antialiased edges trace into 3-5 anchors per straight
    // edge at tol=2.5, low enough that hand-drawn corners stay
    // recognizable but high enough that a rectangle rounds to
    // 4 corner anchors instead of dozens of stair-step segments.
    try {
      sub.simplify(tol);
    } catch {
      // paper.Path.simplify can throw on degenerate inputs (e.g.
      // every point coincident). Falling back to the unsimplified
      // polyline is fine — the resulting vector mask still
      // matches the alpha shape, just with more anchors.
    }
    compound.addChild(sub);
  }

  try {
    return paperPathItemToVectorPath(compound);
  } finally {
    compound.remove();
  }
}

// ─── Marching squares ───────────────────────────────────────────
// Edge-pair table approach. Each marching-squares case has 0, 1,
// or 2 line segments crossing the 2×2 cell. Each segment connects
// two of the cell's four side midpoints (N/E/S/W). For each case
// we enumerate the (entry, exit) pairs of each segment; tracing
// then walks "if you entered side X of this cell, you exit on
// the OTHER end of whichever segment touches X."
//
// The saddle cases (5 and 10) have two separate segments; the
// convention we pick determines whether saddles produce two
// loops or one figure-eight. Standard MS convention: connect
// like-corners (case 5 = TR + BL filled → segments {N,W} and
// {S,E}, NOT {N,E} and {S,W}). That convention treats each
// filled corner as a separate connected component, which is
// what users expect from a typical alpha mask.

type Side = 'N' | 'E' | 'S' | 'W';

/** Per-case enumeration of (entry, exit) pairs. Symmetric — each
 *  segment contributes two entries so we can look up by either
 *  end. */
const CASE_PAIRS: Record<number, [Side, Side][]> = {
  0: [],
  1: [
    ['S', 'W'],
    ['W', 'S'],
  ],
  2: [
    ['S', 'E'],
    ['E', 'S'],
  ],
  3: [
    ['W', 'E'],
    ['E', 'W'],
  ],
  4: [
    ['N', 'E'],
    ['E', 'N'],
  ],
  // Saddle: TR + BL filled → two separate diagonals
  5: [
    ['N', 'W'],
    ['W', 'N'],
    ['S', 'E'],
    ['E', 'S'],
  ],
  6: [
    ['N', 'S'],
    ['S', 'N'],
  ],
  7: [
    ['N', 'W'],
    ['W', 'N'],
  ],
  8: [
    ['N', 'W'],
    ['W', 'N'],
  ],
  9: [
    ['N', 'S'],
    ['S', 'N'],
  ],
  // Saddle: TL + BR filled → two separate diagonals
  10: [
    ['N', 'E'],
    ['E', 'N'],
    ['S', 'W'],
    ['W', 'S'],
  ],
  11: [
    ['N', 'E'],
    ['E', 'N'],
  ],
  12: [
    ['W', 'E'],
    ['E', 'W'],
  ],
  13: [
    ['S', 'E'],
    ['E', 'S'],
  ],
  14: [
    ['S', 'W'],
    ['W', 'S'],
  ],
  15: [],
};

interface Point2 {
  x: number;
  y: number;
}

function marchingSquaresContours(grid: Uint8Array, pw: number, ph: number): Point2[][] {
  const cellW = pw - 1;
  const cellH = ph - 1;

  function cellCase(cx: number, cy: number): number {
    // 2×2 corners using clockwise-from-top-left bit packing:
    // TL=8, TR=4, BR=2, BL=1. Yields the same case numbering as
    // the CASE_PAIRS table.
    const tl = grid[cy * pw + cx] ? 8 : 0;
    const tr = grid[cy * pw + (cx + 1)] ? 4 : 0;
    const br = grid[(cy + 1) * pw + (cx + 1)] ? 2 : 0;
    const bl = grid[(cy + 1) * pw + cx] ? 1 : 0;
    return tl | tr | br | bl;
  }

  function sideToOffset(side: Side): { dx: number; dy: number; opposite: Side } {
    switch (side) {
      case 'N':
        return { dx: 0, dy: -1, opposite: 'S' };
      case 'E':
        return { dx: 1, dy: 0, opposite: 'W' };
      case 'S':
        return { dx: 0, dy: 1, opposite: 'N' };
      case 'W':
        return { dx: -1, dy: 0, opposite: 'E' };
    }
  }

  function sideMidpoint(cx: number, cy: number, side: Side): Point2 {
    switch (side) {
      case 'N':
        return { x: cx + 0.5, y: cy };
      case 'E':
        return { x: cx + 1, y: cy + 0.5 };
      case 'S':
        return { x: cx + 0.5, y: cy + 1 };
      case 'W':
        return { x: cx, y: cy + 0.5 };
    }
  }

  /** Visited bitmask per (cell, segment-index). For non-saddle
   *  cells, segment 0 occupies bit 0 (segment 1 is unused). For
   *  saddle cells (5, 10), bit 0 tracks the first pair, bit 1 the
   *  second. */
  const visited = new Uint8Array(cellW * cellH);

  /** Find the segment index within a cell's CASE_PAIRS that the
   *  given `entry` side touches. Returns -1 if the cell has no
   *  segment touching `entry`. */
  function findSegmentIndex(caseIdx: number, entry: Side): number {
    const pairs = CASE_PAIRS[caseIdx];
    // Pairs are stored bidirectionally — segment 0 is pairs[0]/[1],
    // segment 1 is pairs[2]/[3]. Match the entry side and return
    // the segment index.
    for (let i = 0; i < pairs.length; i++) {
      if (pairs[i][0] === entry) return i >> 1;
    }
    return -1;
  }

  function exitForEntry(caseIdx: number, entry: Side): Side | null {
    const pairs = CASE_PAIRS[caseIdx];
    for (const [a, b] of pairs) {
      if (a === entry) return b;
    }
    return null;
  }

  const contours: Point2[][] = [];

  for (let y = 0; y < cellH; y++) {
    for (let x = 0; x < cellW; x++) {
      const c0 = cellCase(x, y);
      if (c0 === 0 || c0 === 15) continue;
      const pairs = CASE_PAIRS[c0];
      const segCount = pairs.length >> 1;
      for (let seg = 0; seg < segCount; seg++) {
        const segBit = 1 << seg;
        if (visited[y * cellW + x] & segBit) continue;

        // Start tracing this segment. Use the first side in the
        // segment's pair as the "start side" — walk from there.
        const startSide = pairs[seg * 2][0];
        const points: Point2[] = [];

        let cx = x;
        let cy = y;
        let side: Side = startSide;
        // Mark seed visited.
        visited[cy * cellW + cx] |= segBit;
        points.push(sideMidpoint(cx, cy, side));

        // Walk along the segment within the current cell: exit
        // out of `side`'s pair → step to next cell → that side
        // becomes the new entry → look up exit. Loop until we
        // return to start.
        let safety = 0;
        const maxSteps = cellW * cellH * 4;
        while (safety++ < maxSteps) {
          const exit = exitForEntry(cellCase(cx, cy), side);
          if (!exit) break;
          points.push(sideMidpoint(cx, cy, exit));
          const step = sideToOffset(exit);
          const nx = cx + step.dx;
          const ny = cy + step.dy;
          if (nx < 0 || ny < 0 || nx >= cellW || ny >= cellH) break;
          const newEntry = step.opposite;
          const nextCase = cellCase(nx, ny);
          if (nextCase === 0 || nextCase === 15) break;
          const nextSegIdx = findSegmentIndex(nextCase, newEntry);
          if (nextSegIdx < 0) break;
          const nextBit = 1 << nextSegIdx;
          if (nx === x && ny === y && nextSegIdx === seg) {
            // Closed back to start. Done.
            break;
          }
          if (visited[ny * cellW + nx] & nextBit) break;
          visited[ny * cellW + nx] |= nextBit;
          cx = nx;
          cy = ny;
          side = newEntry;
        }

        if (points.length >= 3) {
          // De-duplicate the start point if the trace wrapped
          // back to it as the last point (most loops do).
          const first = points[0];
          const last = points[points.length - 1];
          if (Math.abs(first.x - last.x) < 1e-6 && Math.abs(first.y - last.y) < 1e-6) {
            points.pop();
          }
          if (points.length >= 3) contours.push(points);
        }
      }
    }
  }

  return contours;
}
